import { useMemo, useState } from 'react';

import { Box, IconButton, Typography } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const BASE_INCREMENT = 250;
const INCREMENT_STEP = 250;

function cutAtWordBoundary(text: string, limit: number): number {
  if (limit >= text.length) return text.length;

  let pos = limit;

  while (pos < text.length && text[pos] !== ' ' && text[pos] !== '\n') {
    pos += 1;
  }

  return pos;
}

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  container: {
    marginTop: spacing(0.5),
  },
  title: ({ fromMe }: { fromMe: boolean }) => ({
    fontSize: typography.pxToRem(11),
    fontWeight: 500,
    color: fromMe ? 'white' : palette.text.primary,
    letterSpacing: 0.5,
  }),
  iconButton: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    padding: 2,
    color: alpha(palette.text.primary, 0.45),
  },
  text: ({ fromMe }: { fromMe: boolean }) => ({
    color: fromMe ? 'white' : alpha(palette.text.primary, 0.7),
    letterSpacing: 0.15,
    wordBreak: 'break-word',
    fontSize: typography.pxToRem(13),
  }),
}));

interface ExpandableTranscriptProps {
  text: string;
  fromMe?: boolean;
}

export default function ExpandableTranscript({
  text,
  fromMe = false,
}: ExpandableTranscriptProps) {
  const classes = useStyles({ fromMe });

  const initialLimit = useMemo(
    () => cutAtWordBoundary(text, BASE_INCREMENT),
    [text],
  );

  const [visibleEnd, setVisibleEnd] = useState(initialLimit);
  const [clickCount, setClickCount] = useState(0);

  const hasMore = visibleEnd < text.length;

  const displayedText = useMemo(
    () => text.slice(0, visibleEnd),
    [text, visibleEnd],
  );

  const handleShowMore = () => {
    setVisibleEnd(prev => {
      if (prev >= text.length) return text.length;

      const dynamicIncrement = BASE_INCREMENT + clickCount * INCREMENT_STEP;

      const next = cutAtWordBoundary(text, prev + dynamicIncrement);

      return next >= text.length ? text.length : next;
    });

    setClickCount(prev => prev + 1);
  };

  if (!text) return null;

  const isShortText = text.length <= BASE_INCREMENT;

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Transcrição do áudio</Typography>

      <Box style={{ position: 'relative' }}>
        <Typography
          variant="body2"
          className={classes.text}
          style={{
            paddingRight: hasMore ? 24 : 0,
          }}
        >
          {isShortText ? text : displayedText}
          {!isShortText && hasMore && '...'}
        </Typography>

        {!isShortText && hasMore && (
          <IconButton
            aria-label="Ver mais"
            onClick={handleShowMore}
            className={classes.iconButton}
            size="small"
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
